import React, { useMemo, useState, useCallback, useEffect } from 'react';
import useUI from '../hooks/useUI';
import useData from '../hooks/useData';
import useApp from '../hooks/useApp';
import IconClose from '../icons/IconClose';
import IconHeart from '../icons/IconHeart';
import IconHeartFilled from '../icons/IconHeartFilled';
import { uniq, filter } from 'underscore';
import useRecord from '../hooks/useRecord';

const ExhibitorList = () => {
	const { ExhibitorList, toggleUI, toggleExhibitorInfo } = useUI();
	const { exhibitorInfo, toggleFavourite } = useData();
	const { isCHT, setSelectedBooth, setActiveFloor, setSelectedExhibitor, exhibitorSection } = useApp();
	const [searchTerm, setSearchTerm] = useState('');
	const [currentLetter, setCurrentLetter] = useState('#');
	const { makeRecord } = useRecord();

	useEffect(() => {
		if (exhibitorSection) setCurrentLetter('#');
	}, [exhibitorSection]);

	const exhibitorsBySection = useMemo(() => {
		const key = isCHT ? '區域-中文' : '區域-英文';
		const exhibitorsBySection = exhibitorInfo.reduce((acc, exhibitor) => {
			const section = exhibitor[key];
			if (!acc[section]) acc[section] = [];
			acc[section].push(exhibitor);
			return acc;
		}, {});
		return exhibitorsBySection;
	}, [exhibitorInfo, isCHT]);

	const exhibitorsCurrentSection = useMemo(() => {
		if (exhibitorSection === 'all') return exhibitorInfo;
		return exhibitorsBySection[exhibitorSection] || [];
	}, [exhibitorInfo, exhibitorSection, exhibitorsBySection]);

	const firstLetters = useMemo(() => {
		const key = isCHT ? '首字母-中文' : '首字母-英文';
		const uniqueFirstLetters = uniq(exhibitorsCurrentSection.map(exhibitor => exhibitor[key]));

		if (isCHT) {
			uniqueFirstLetters.sort((a, b) => {
				return a.localeCompare(b, 'zh-Hant');
			});
		}

		if (!isCHT) {
			uniqueFirstLetters.sort((a, b) => {
				return a.localeCompare(b);
			});
		}

		uniqueFirstLetters.unshift('#');
		uniqueFirstLetters.splice(uniqueFirstLetters.indexOf(''), 1);

		return uniqueFirstLetters;
	}, [exhibitorsCurrentSection, isCHT]);

	const exhibitorsByLetter = useMemo(() => {
		if (currentLetter === '#') return exhibitorsCurrentSection;
		const key = isCHT ? '首字母-中文' : '首字母-英文';
		const exhibitorsByLetter = filter(exhibitorsCurrentSection, exhibitor => exhibitor[key] === currentLetter);
		return exhibitorsByLetter;
	}, [currentLetter, exhibitorsCurrentSection, isCHT]);

	const filteredExhibitorInfo = useMemo(() => {
		if (!searchTerm) return exhibitorsByLetter;
		if (searchTerm) return exhibitorsByLetter.filter((exhibitor) => {
			const term = searchTerm.toLowerCase();
			return exhibitor['廠商名稱-中文'].toLowerCase().includes(term) || exhibitor['攤位號碼'].toLowerCase().includes(term) || exhibitor['廠商名稱-英文'].toLowerCase().includes(term)
		});
	}, [exhibitorsByLetter, searchTerm]);

	const closePanel = useCallback(() => {
		toggleUI('ExhibitorList');
	}, [toggleUI]);

	const toggleFavouriteExhibitor = useCallback((boothNumber) => {
		toggleFavourite(boothNumber);
	}, [toggleFavourite]);

	// const goToBooth = useCallback((boothNumber, floor) => {
	// 	if (floor === undefined) return;
	// 	setActiveFloor(floor);
	// 	setSelectedBooth(boothNumber);
	// 	toggleUI('ExhibitorList');
	// }, [setActiveFloor, setSelectedBooth, toggleUI]);

	// const showExhibitorInfo = useCallback((boothNumber) => {
	// 	setSelectedExhibitor(boothNumber);
	// 	toggleExhibitorInfo();
	// }, [setSelectedExhibitor, toggleExhibitorInfo]);

	const chooseExhibitor = useCallback((boothNumber, floor) => {
		if (floor !== undefined) setActiveFloor(floor);
		setSelectedBooth(boothNumber);
		setSelectedExhibitor(boothNumber);
		toggleExhibitorInfo();
	}, [setActiveFloor, setSelectedBooth, setSelectedExhibitor, toggleExhibitorInfo]);

	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	return (
		<div id='ExhibitorList' className={`ui-panel booth ${ExhibitorList ? 'active' : ''}`}>
			<div className='panel-heading'>{exhibitorSection === 'all' ? (isCHT ? '全部廠商名單' : 'All Exhibitors') : exhibitorSection}</div>
			<div className='panel-body'>
				<div className='flex-controls'>
					<div className='search'>
						<input type='text' placeholder={isCHT ? '搜尋廠商名稱或攤位號碼' : 'Search exhibitor name or booth number'} value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value) }} />
					</div>
				</div>

				<div className='flex-body'>
					<div className='alphabets'>
						{firstLetters.map((letter, index) => {
							return (
								<div className={`alphabet ${currentLetter === letter ? 'active' : ''}`} key={`alphabet${index}`} onClick={() => { setCurrentLetter(letter) }}>{letter}</div>
							)
						})}
					</div>

					<div className='exhibitors'>
						{filteredExhibitorInfo.map((exhibitor, index) => {
							const exhibitorName = isCHT ? exhibitor['廠商名稱-中文'] : exhibitor['廠商名稱-英文'];
							const booth = exhibitor['攤位號碼'];

							return (
								<div className='exhibitor' key={`exhibitorLinkTableRow${index}`}>
									<div className='name' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{exhibitorName}</div>
									<div className='booth-number' onClick={() => { chooseExhibitor(booth, exhibitor.floor); recordExhibitorClicked(booth); }}>{booth}</div>
									<div className='icon' onClick={() => { toggleFavouriteExhibitor(booth) }}>
										{exhibitor.favourite ? <IconHeartFilled /> : <IconHeart />}
									</div>
								</div>
							)
						})}
					</div>
				</div>
			</div>

			<IconClose onClick={closePanel} />
		</div>
	)
}

export default ExhibitorList;